import * as React from 'react';
import { AlertOctagonLine, Refresh1Line } from '@mingcute/react';

import { cn } from '../lib/cn';

export interface ErrorStateProps extends React.HTMLAttributes<HTMLDivElement> {
  title?: string;
  description?: string;
  retryLabel?: string;
  onRetry?: () => void;
  icon?: React.ReactNode;
}

export const ErrorState = React.forwardRef<HTMLDivElement, ErrorStateProps>(
  (
    {
      title = 'Something went wrong',
      description = 'We hit an error while loading this. Please try again.',
      retryLabel = 'Retry',
      onRetry,
      icon,
      className,
      ...props
    },
    ref,
  ) => (
    <div
      ref={ref}
      role="alert"
      className={cn(
        'flex flex-col items-center justify-center gap-3 px-6 py-12 text-center',
        className,
      )}
      {...props}
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-danger-light)] text-[var(--color-danger)]">
        {icon ?? <AlertOctagonLine size={40} />}
      </div>
      <h3 className="text-base font-semibold text-[var(--color-fg)]">{title}</h3>
      {description && (
        <p className="max-w-md text-sm text-[var(--color-fg-secondary)]">{description}</p>
      )}
      {onRetry && (
        <button
          type="button"
          onClick={onRetry}
          className={cn(
            'mt-2 inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-medium',
            'bg-[var(--color-accent)] text-[var(--tvl-on-accent)] hover:bg-[var(--color-accent-hover)]',
            'focus-visible:outline-none',
            'transition-colors',
          )}
        >
          <Refresh1Line size={16} />
          {retryLabel}
        </button>
      )}
    </div>
  ),
);
ErrorState.displayName = 'ErrorState';
